"use client";
import { Divider, Paper, Stack, useMediaQuery } from "@mui/material";
import React from "react";
import { FormByIdDocument, ContentForm } from "@cbex/data-access";
import { Form, JSONSchema7 } from "@bpipartners/rjsf-web-ui";
import validator from "@bpipartners/rjsf-validator-ajv";
import { useTranslations } from "next-intl";
import cloneDeep from "lodash/cloneDeep";
import FormIndexCompoment from "../ui/FormIndexCompoment";
import { ErrorBoundary } from "react-error-boundary";
import { CustomTypographyComponent } from "@cbex/ui/text";
import { useTheme } from "@cbex/utils/theme";
import { enqueueSnackbar } from "notistack";
import { useWindowDimensions } from "@cbex/utils/hooks";
import { useQuery } from "@apollo/client";
import { FallbackErrorComponent } from "@cbex/ui/fallback";

interface ViewFormCompositeProps {
  formID: string;
  useLiveValidate: boolean;
  inputFormData: any;
  formStatus: any;
  children?: any;
  hideFormIndex?: boolean;
  hideFormTitle?: boolean;
  onFormvalueChanged?: (
    formvalue: any,
    submitted: boolean,
    schema: any,
    uiSchema: any
  ) => void;
  onSubmitClicked?: (
    formData: any,
    submitted: boolean,
    schema: any,
    uiSchema: any
  ) => void;
}

export const ViewFormComposite = React.forwardRef(
  (props: ViewFormCompositeProps, ref) => {
    const {
      formID,
      useLiveValidate,
      inputFormData,
      onFormvalueChanged,
      formStatus,
      onSubmitClicked,
      hideFormIndex,
      hideFormTitle,
    } = props;

    const { data: formData, loading: formLoading } = useQuery(
      FormByIdDocument,
      {
        variables: {
          id: formID,
        },
        fetchPolicy: "network-only",
      }
    );
    const t = useTranslations();
    const theme = useTheme();
    const submitFormRef = React.createRef();

    const smallDevice = useMediaQuery(theme.breakpoints.down(1090));
    const windowSize = useWindowDimensions();
    const [formContent, setFormContent] = React.useState<ContentForm>();
    const [useLiveValidateState, setUseLiveValidateState] =
      React.useState(false);
    const [errorsIndexes, setErrorIndexs] = React.useState<string[]>([]);
    const onSubmit = (val: any, e: any) => {
      if (onSubmitClicked) {
        onSubmitClicked(
          val.formData,
          true,
          formContent?.formSchema,
          formContent?.uiSchema
        );
      }
    };

    const onValueChanged = (val: any, error: any) => {
      //@ts-ignore
      setFormContent({
        ...formContent,
        defaultData: val.formData,
      });
      if (onFormvalueChanged) {
        onFormvalueChanged(
          val.formData,
          false,
          formContent?.formSchema,
          formContent?.uiSchema
        );
      }
    };

    const transformErrors = (errors: any[]) =>
      errors.map((error: any) => {
        if (useLiveValidate && useLiveValidateState === false) {
          setUseLiveValidateState(true);
        }
        if (error.name === "required") {
          error.message = t("labels.requiredField");
        }
        return error;
      });
    React.useEffect(() => {
      if (!formLoading && formData && formData.FormByID) {
        setFormContent(formData.FormByID);
      } else {
        setFormContent(undefined);
      }
    }, [formData, formLoading]);

    React.useEffect(() => {
      if (!formLoading && formData && formData.FormByID) {
        if (inputFormData) {
          setFormContent({
            defaultData: inputFormData,
            formID: formID,
            formSchema: formData.FormByID.formSchema,
            uiSchema:
              formContent && formContent.uiSchema
                ? formContent?.uiSchema
                : formData.FormByID.uiSchema,
            description: formData.FormByID.description,
            name: formData.FormByID.name,
          });
        } else {
          setFormContent(formData.FormByID);
        }
      } else {
        setFormContent(undefined);
      }
    }, [inputFormData, formData, formLoading]);

    const handleOnSectionClicked = (item: string) => {
      const temp = cloneDeep(formContent?.uiSchema);

      Object.keys(temp).forEach((element) => {
        if (item === element) {
          temp[element]["ui:options"] = { hidden: false };
        } else {
          temp[element]["ui:options"] = { hidden: true };
        }
      });

      //@ts-ignore
      setFormContent({
        ...formContent,
        uiSchema: temp,
      });
    };

    const handleOnError = (errors: any[]) => {
      const destructOfArray = [...new Set(errors.map((obj) => obj.schemaPath))];
      const subStringOfErrorArray = destructOfArray.map(
        (item) => item.split("/")[2] // schemaPath is like: "#/properties/Eindconclusie/required"
      );
      const finalStringArray = [
        ...new Set(subStringOfErrorArray.map((obj) => obj)),
      ];
      setErrorIndexs(finalStringArray);
      setTimeout(() => {
        enqueueSnackbar(t("feedback.missingValues"), {
          variant: "error",
        });
      }, 1000);
    };

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        if (onSubmitClicked) {
          //@ts-ignore
          submitFormRef.current.click();
        }
      },
    }));

    const getHideMenu =
      (formContent &&
        formContent.uiSchema &&
        formContent.uiSchema["ui:options"] &&
        formContent.uiSchema["ui:options"].hideMenu) ||
      false;

    return (
      <Stack id="ViewFormComposite" height={"100"}>
        {!formLoading &&
          formContent &&
          formContent &&
          formContent.formSchema && (
            <Stack alignItems={smallDevice ? "center" : ""}>
              <Paper>
                {!hideFormTitle && (
                  <Stack padding={2} spacing={2}>
                    <CustomTypographyComponent variant="h4">
                      {formContent.name}
                    </CustomTypographyComponent>
                    <Divider />
                  </Stack>
                )}
                <Stack direction="row" spacing={2} padding={2}>
                  {!getHideMenu && (
                    <FormIndexCompoment
                      errorIndexes={errorsIndexes}
                      onSectionClicked={handleOnSectionClicked}
                      data={formContent}
                    />
                  )}
                  <Stack
                    height={
                      windowSize.height
                        ? windowSize.height -
                          theme.dimensions.totalTopbarHeight * 2 -
                          theme.dimensions.margin * 2
                        : "80%"
                    }
                    spacing={2}
                    padding={2}
                    minWidth={300}
                    sx={{ overflowY: "auto", overflowX: "hidden" }}
                  >
                    <ErrorBoundary FallbackComponent={FallbackErrorComponent}>
                      <Form
                        schema={
                          formContent &&
                          formContent.formSchema !== undefined &&
                          formContent.formSchema
                        }
                        uiSchema={
                          formContent &&
                          formContent.uiSchema !== undefined &&
                          formContent.uiSchema
                        }
                        onSubmit={onSubmit}
                        showErrorList={false}
                        readonly={formStatus}
                        onError={handleOnError}
                        noHtml5Validate={true}
                        validator={validator}
                        onChange={onValueChanged}
                        liveValidate={useLiveValidateState}
                        experimental_defaultFormStateBehavior={{
                          constAsDefaults: "skipOneOf",
                        }}
                        formData={
                          (formContent && formContent.defaultData) || {}
                        }
                        transformErrors={transformErrors}
                      >
                        {props && props.children && props.children}

                        <button
                          //@ts-ignore
                          ref={submitFormRef}
                          type="submit"
                          style={{ display: "none" }}
                        />
                      </Form>
                    </ErrorBoundary>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}
      </Stack>
    );
  }
);

ViewFormComposite.displayName = "ViewFormComposite";
export default ViewFormComposite;
